import React, { useState, useMemo, useRef, useEffect } from 'react';
import SubTabs from '../../layout/SubTabs';
import DimensionHeader from '../../common/DimensionHeader';
import { useTheme } from '../../../utils/ThemeSwitcher';
import { AnimatedNumber, AnimatedCard } from '../../common/AnimationUtils';
import * as d3 from 'd3';
import {
  BarChart3,
  GitBranch,
  TrendingDown,
  Users,
  Target,
  AlertTriangle,
  CheckCircle,
  XCircle,
  ChevronDown,
  ChevronUp,
  ArrowRight,
  ArrowUpDown,
  Download,
  Filter,
  Eye,
  Layers,
  Activity,
  Zap,
  Info,
  RefreshCw,
  Maximize2,
  Table,
  Shield,
  FileWarning,
  AlertCircle
} from 'lucide-react';
import './SurveyFlowSection.css';
import PulseBeacon from './PulseBeacon';
import CollapsibleSection from './CollapsibleSection';
import ScoreCard from '../charts/ScoreCard';
import EmptyStateCelebration from './EmptyStateCelebration';
 
// CSS Keyframe Animations
const animationStyles = `
  @keyframes shine {
    0% { left: -100%; }
    100% { left: 200%; }
  }
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(-5px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes slideInLeft {
    from { opacity: 0; transform: translateX(-20px); }
    to { opacity: 1; transform: translateX(0); }
  }
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.7; }
  }
  @keyframes scaleIn {
    from { opacity: 0; transform: scale(0.95); }
    to { opacity: 1; transform: scale(1); }
  }
  @keyframes sfBarGrow {
    from { transform: scaleX(0); }
    to { transform: scaleX(1); }
  }
`;
 
// ============== SANKEY DIAGRAM COMPONENT ==============
// Module scope on purpose: defined inside the parent, its identity changed
// on every parent render, so React remounted the whole diagram per state
// tick (AnimatedNumber counters, min-count typing) — killing the link
// entrance transition at stroke-width 0 and stealing input focus.
  const SankeyDiagram = ({ data, minCount, isDarkMode, colors, isMobile = false }) => {
    
    const ref = useRef();
    const tooltipRef = useRef();
    // Instance-unique prefix: this dashboard can be mounted twice at once
    // (workspace preview + expanded modal). Generic gradient ids collide
    // across instances and url(#id) resolves to the wrong document node.
    const uidRef = useRef('sfg' + Math.random().toString(36).slice(2, 8));
    
    useEffect(() => {
      if (!data || !data.transitions || !ref.current) return;
      
      const svg = d3.select(ref.current);
      svg.selectAll("*").remove();
      
      const filteredTransitions = data.transitions.filter(t => t.value >= minCount);
      
      if (filteredTransitions.length === 0) {
        svg.append('text')
          .attr('x', '50%')
          .attr('y', '50%')
          .attr('text-anchor', 'middle')
          .style('fill', colors.textMuted)
          .text('No transitions meet the minimum count threshold');
        return;
      }
      
      const variables = data.variable_order || [];
      const width = isMobile ? Math.max(500, variables.length * 180) : Math.max(800, variables.length * 250);
      const height = 500;
      const margin = isMobile 
        ? { top: 50, right: 60, bottom: 40, left: 60 }
        : { top: 50, right: 120, bottom: 40, left: 120 };
      const innerWidth = width - margin.left - margin.right;
      const innerHeight = height - margin.top - margin.bottom;
      
      // Build nodes by variable
      const nodesByVariable = {};
      filteredTransitions.forEach(t => {
        if (!nodesByVariable[t.source_variable]) nodesByVariable[t.source_variable] = new Set();
        if (!nodesByVariable[t.target_variable]) nodesByVariable[t.target_variable] = new Set();
        nodesByVariable[t.source_variable].add(t.source_value);
        nodesByVariable[t.target_variable].add(t.target_value);
      });
      
      const colorScale = d3.scaleOrdinal()
        .domain(variables)
        .range(['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#EC4899', '#14B8A6']);
      
      // Calculate node positions
      const columnWidth = innerWidth / Math.max(1, variables.length - 1);
      const nodeWidth = 24;
      const nodes = [];
      const nodePositions = new Map();
      
      variables.forEach((variable, varIndex) => {
        const values = Array.from(nodesByVariable[variable] || []);
        const x = varIndex * columnWidth;
        const nodeSpacing = Math.max(10, innerHeight / Math.max(1, values.length + 1));
        
        values.forEach((value, valueIndex) => {
          const nodeId = `${variable}_${value}`;
          const y = (valueIndex + 1) * nodeSpacing;
          
          const node = {
            id: nodeId,
            variable,
            value,
            x,
            y: y - 18,
            width: nodeWidth,
            height: 36,
            varIndex
          };
          
          nodes.push(node);
          nodePositions.set(nodeId, node);
        });
      });
      
      // Create gradient definitions
      const defs = svg.append('defs');
      
      // Create gradients for each transition — unique ids per instance and
      // explicit endpoints so the gradient runs along its own link.
      filteredTransitions.forEach((t, i) => {
        const sourceColor = colorScale(t.source_variable);
        const targetColor = colorScale(t.target_variable);
        const sn = nodePositions.get(`${t.source_variable}_${t.source_value}`);
        const tn = nodePositions.get(`${t.target_variable}_${t.target_value}`);
        
        const gradient = defs.append('linearGradient')
          .attr('id', `${uidRef.current}-link-${i}`)
          .attr('gradientUnits', 'userSpaceOnUse')
          .attr('x1', sn ? sn.x + sn.width : 0)
          .attr('y1', sn ? sn.y + sn.height / 2 : 0)
          .attr('x2', tn ? tn.x : 0)
          .attr('y2', tn ? tn.y + tn.height / 2 : 0);
        
        gradient.append('stop')
          .attr('offset', '0%')
          .attr('stop-color', sourceColor)
          .attr('stop-opacity', 0.7);
        
        gradient.append('stop')
          .attr('offset', '100%')
          .attr('stop-color', targetColor)
          .attr('stop-opacity', 0.7);
      });
      
      const g = svg.append('g')
        .attr('transform', `translate(${margin.left},${margin.top})`);
      
      // Create tooltip div
      const tooltip = d3.select(tooltipRef.current);
      
      // Draw links with animations
      const maxValue = d3.max(filteredTransitions, d => d.value);
      const totalTransitions = d3.sum(filteredTransitions, d => d.value);
      
      const links = g.selectAll('.sankey-link')
        .data(filteredTransitions)
        .enter()
        .append('path')
        .attr('class', 'sankey-link')
        .attr('d', d => {
          const sourceNode = nodePositions.get(`${d.source_variable}_${d.source_value}`);
          const targetNode = nodePositions.get(`${d.target_variable}_${d.target_value}`);
          
          if (!sourceNode || !targetNode) return '';
          
          const x0 = sourceNode.x + sourceNode.width;
          const y0 = sourceNode.y + sourceNode.height / 2;
          const x1 = targetNode.x;
          const y1 = targetNode.y + targetNode.height / 2;
          
          const dx = x1 - x0;
          const cx0 = x0 + dx * 0.4;
          const cx1 = x1 - dx * 0.4;
          
          return `M${x0},${y0}C${cx0},${y0} ${cx1},${y1} ${x1},${y1}`;
        })
        .style('fill', 'none')
        .style('stroke', (d, i) => `url(#${uidRef.current}-link-${i})`)
        .attr('data-width', d => Math.max(3, (d.value / maxValue) * 18))
        .style('stroke-width', d => Math.max(3, (d.value / maxValue) * 18))
        .style('stroke-opacity', 0.6)
        .style('cursor', 'pointer')
        .on('mouseover', function(event, d) {
          d3.select(this)
            .style('stroke-opacity', 1)
            .style('stroke-width', function() {
              return parseFloat(d3.select(this).attr('data-width')) * 1.3;
            });
          
          // Fade other links
          links.filter(other => other !== d)
            .style('stroke-opacity', 0.2);
          
          // Show tooltip
          const pct = ((d.value / totalTransitions) * 100).toFixed(1);
          tooltip
            .style('opacity', 1)
            .style('left', (event.pageX + 15) + 'px')
            .style('top', (event.pageY - 10) + 'px')
            .html(`
              <div style="font-weight: 600; margin-bottom: 6px; color: ${colors.text}">
                ${d.source_value} → ${d.target_value}
              </div>
              <div style="display: flex; justify-content: space-between; gap: 20px;">
                <span style="color: ${colors.textMuted}">Responses:</span>
                <span style="font-weight: 600; color: ${colors.primary}">${d.value.toLocaleString()}</span>
              </div>
              <div style="display: flex; justify-content: space-between; gap: 20px;">
                <span style="color: ${colors.textMuted}">Share:</span>
                <span style="font-weight: 600; color: ${colors.success}">${pct}%</span>
              </div>
              <div style="display: flex; justify-content: space-between; gap: 20px; margin-top: 4px; padding-top: 4px; border-top: 1px solid ${colors.border}">
                <span style="color: ${colors.textMuted}">From:</span>
                <span style="color: ${colors.text}">${d.source_variable}</span>
              </div>
              <div style="display: flex; justify-content: space-between; gap: 20px;">
                <span style="color: ${colors.textMuted}">To:</span>
                <span style="color: ${colors.text}">${d.target_variable}</span>
              </div>
            `);
        })
        .on('mouseout', function() {
          d3.select(this)
            .style('stroke-opacity', 0.6)
            .style('stroke-width', d3.select(this).attr('data-width'));
          
          links.style('stroke-opacity', 0.6);
          tooltip.style('opacity', 0);
        });
      
      // Gentle group fade-in — starts from a visible state, so an
      // interrupted transition still leaves the links on screen.
      links
        .style('opacity', 0.35)
        .transition()
        .duration(500)
        .ease(d3.easeCubicOut)
        .style('opacity', 1);
      
      // Draw nodes with animations
      const nodeGroups = g.selectAll('.sankey-node')
        .data(nodes)
        .enter()
        .append('g')
        .attr('class', 'sankey-node')
        .attr('transform', d => `translate(${d.x},${d.y})`)
        .style('cursor', 'pointer')
        .style('opacity', 0);
      
      // Add glow filter
      const filter = defs.append('filter')
        .attr('id', 'node-glow')
        .attr('x', '-50%')
        .attr('y', '-50%')
        .attr('width', '200%')
        .attr('height', '200%');
      
      filter.append('feGaussianBlur')
        .attr('stdDeviation', '3')
        .attr('result', 'coloredBlur');
      
      const feMerge = filter.append('feMerge');
      feMerge.append('feMergeNode').attr('in', 'coloredBlur');
      feMerge.append('feMergeNode').attr('in', 'SourceGraphic');
      
      nodeGroups.append('rect')
        .attr('width', d => d.width)
        .attr('height', d => d.height)
        .style('fill', d => colorScale(d.variable))
        .style('stroke', isDarkMode ? '#1F1F23' : '#FFFFFF')
        .style('stroke-width', 2)
        .style('rx', 6)
        .on('mouseover', function(event, d) {
          d3.select(this)
            .style('filter', 'url(#node-glow)')
            .transition()
            .duration(200)
            .attr('width', d.width + 4)
            .attr('height', d.height + 4)
            .attr('x', -2)
            .attr('y', -2);
          
          // Highlight connected links
          links
            .style('stroke-opacity', link => 
              (link.source_variable === d.variable && link.source_value === d.value) ||
              (link.target_variable === d.variable && link.target_value === d.value)
                ? 1 : 0.15
            );
        })
        .on('mouseout', function(event, d) {
          d3.select(this)
            .style('filter', null)
            .transition()
            .duration(200)
            .attr('width', d.width)
            .attr('height', d.height)
            .attr('x', 0)
            .attr('y', 0);
          
          links.style('stroke-opacity', 0.6);
        });
      
      nodeGroups.append('text')
        .attr('x', d => d.width + 8)
        .attr('y', d => d.height / 2)
        .attr('dy', '0.35em')
        .style('font-size', '13px')
        .style('font-weight', '500')
        .style('fill', colors.text)
        .text(d => d.value.length > 18 ? d.value.substring(0, 15) + '...' : d.value);
      
      // Animate nodes entrance
      nodeGroups
        .transition()
        .duration(600)
        .delay((d, i) => Math.min(700, d.varIndex * 120))
        .ease(d3.easeCubicOut)
        .style('opacity', 1);
      
      // Variable labels with better styling
      variables.forEach((variable, index) => {
        const labelGroup = g.append('g')
          .attr('transform', `translate(${index * columnWidth + nodeWidth / 2}, -25)`)
          .style('opacity', 0);
        
        labelGroup.append('rect')
          .attr('x', -50)
          .attr('y', -12)
          .attr('width', 100)
          .attr('height', 24)
          .attr('rx', 12)
          .style('fill', `${colorScale(variable)}20`);
        
        labelGroup.append('text')
          .attr('text-anchor', 'middle')
          .attr('dy', '0.35em')
          .style('font-size', '13px')
          .style('font-weight', '600')
          .style('fill', colorScale(variable))
          .text(variable.length > 15 ? variable.substring(0, 12) + '...' : variable);
        
        labelGroup
          .transition()
          .duration(400)
          .delay(index * 100)
          .style('opacity', 1);
      });
      
      svg.attr('width', width).attr('height', height);
      
    }, [data, minCount, isDarkMode, colors, isMobile]);
    
    return (
      <div style={{ position: 'relative', minWidth: 0 }}>
        {/* No scroll wrapper here — the call site provides one. Nesting two
            scroll containers caused the outer one to win and clip the inner. */}
        <svg
          ref={ref}
          style={{
            minWidth: isMobile ? '500px' : '800px',
            display: 'block',
          }}
        />
        {/* Tooltip element */}
        <div 
          ref={tooltipRef}
          style={{
            position: 'fixed',
            opacity: 0,
            backgroundColor: isDarkMode ? '#2A2A2E' : '#FFFFFF',
            border: `1px solid ${colors.border}`,
            borderRadius: '10px',
            padding: '12px 16px',
            fontSize: '13px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
            pointerEvents: 'none',
            zIndex: 1000,
            minWidth: '180px',
            transition: 'opacity 0.2s ease'
          }}
        />
      </div>
    );
  };
 
 
// Hoisted to module scope: defining this inside the section component gave it a new
// identity on every re-render, which remounted every card and restarted the load animation.
const MetricCard = ({ colors, isDarkMode, isMobile, icon: Icon, title, value, subtitle, color, onClick, warning, severity, animDelay = 0 }) => {
  const [isHovered, setIsHovered] = useState(false);
  const sevColor = severity === 'critical' ? '#b91c1c' : severity === 'warning' ? '#b45309' : null;
  const sevColorDark = severity === 'critical' ? '#f87171' : severity === 'warning' ? '#fbbf24' : null;
  const iconColor = isDarkMode ? (sevColorDark || '#60a5fa') : (sevColor || '#2563EB');
 
  return (
    <AnimatedCard delay={animDelay} translateY={12}>
    <div
      className="nsx-metric-card"
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        position: 'relative',
        padding: '18px',
        backgroundColor: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.6)',
        borderRadius: '8px',
        border: `0.5px solid ${isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        transition: 'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.25s ease',
        overflow: 'hidden',
        cursor: onClick ? 'pointer' : 'default',
        fontFamily: "'Outfit', -apple-system, sans-serif",
      }}
    >
      {severity && (
        <svg
          width="30" height="30" viewBox="0 0 30 30"
          style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none', overflow: 'visible' }}
        >
          <path
            d="M 28 0 L 8 0 A 8 8 0 0 0 0 8 L 0 28"
            stroke={iconColor} strokeWidth="2" fill="none"
            strokeLinecap="round" opacity={0.9}
          />
        </svg>
      )}
 
      <PulseBeacon severity={severity} isDarkMode={isDarkMode} />
 
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '14px',
        minWidth: 0,
      }}>
        <div style={{
          width: '36px',
          height: '36px',
          borderRadius: '5px',
          background: severity
            ? (isDarkMode
                ? `linear-gradient(135deg, ${iconColor}22, ${iconColor}11)`
                : `linear-gradient(135deg, ${iconColor}18, ${iconColor}08)`)
            : 'linear-gradient(135deg, rgba(37,99,235,0.14), rgba(217,119,6,0.12))',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          color: iconColor,
          transition: 'transform 0.3s ease',
          transform: isHovered ? 'scale(1.08)' : 'scale(1)',
        }}>
          <Icon size={16} strokeWidth={1.75} />
        </div>
        <div style={{
          flex: 1,
          minWidth: 0,
          // Left-align on mobile — the symmetric-centered layout was causing
          // the value column to look shifted right on narrow viewports.
          // Centered remains on desktop where there's room for it to feel balanced.
          textAlign: isMobile ? 'left' : 'center',
          overflow: 'hidden',
        }}>
          <div style={{
            fontSize: '10px',
            fontWeight: 500,
            letterSpacing: '0.16em',
            textTransform: 'lowercase',
            color: colors.textMuted,
            marginBottom: '10px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}>
            {title}
          </div>
          <div
            className="metric-value"
            style={{
              // Fluid sizing — shrinks on narrow viewports so large numbers
              // like "202,301,002" no longer spill past the card edge.
              // Use cqi (container query) with vw fallback so it responds
              // to the card width on modern browsers.
              fontSize: 'clamp(16px, 4.6vw, 30px)',
              fontWeight: 500,
              letterSpacing: '-0.02em',
              fontVariantNumeric: 'tabular-nums',
              color: colors.text,
              lineHeight: 1.05,
              marginBottom: '6px',
              // block, not inline-flex — inline-flex breaks text-overflow:ellipsis
              display: 'flex',
              alignItems: 'center',
              justifyContent: isMobile ? 'flex-start' : 'center',
              gap: '8px',
              maxWidth: '100%',
              minWidth: 0,         // critical for flex-child shrinking
              whiteSpace: 'nowrap',
              overflow: 'hidden',
            }}
          >
            <span style={{
              // The inner span is what actually clips — flex children need
              // their own overflow+ellipsis to truncate cleanly.
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
              <AnimatedNumber value={value} duration={800} />
            </span>
            {warning && (
              <span
                title={warning}
                style={{
                  cursor: 'help',
                  display: 'inline-flex',
                  alignItems: 'center',
                  color: isDarkMode ? '#fbbf24' : '#b45309',
                }}
              >
                <AlertTriangle size={16} strokeWidth={2} />
              </span>
            )}
          </div>
          {subtitle && (
            <div style={{
              fontSize: '11.5px',
              fontWeight: 400,
              letterSpacing: '-0.005em',
              color: colors.textMuted,
              lineHeight: 1.4,
              // Wrap rather than clip so "Highest completion rate" stays visible
              whiteSpace: 'normal',
              wordBreak: 'break-word',
            }}>
              {subtitle}
            </div>
          )}
        </div>
        {!isMobile && (
          <div style={{ width: '36px', flexShrink: 0 }} aria-hidden="true" />
        )}
      </div>
    </div>
    </AnimatedCard>
  );
};

// Hoisted to module scope for the same reason as MetricCard: an inner component
// remounts on every parent re-render and restarts its fill animation.
const FunnelBar = ({ colors, isDarkMode, getSeverityColor, startDelay = 0, variable, responses, total, dropoffRate, severity, isLast, index = 0 }) => {
  const percentage = total > 0 ? (responses / total) * 100 : 0;
  const [isHovered, setIsHovered] = useState(false);
  const [isAnimated, setIsAnimated] = useState(false);
  
  // Sequenced fill: wait for the card to float in (startDelay), stagger by row,
  // then flip on a painted frame so the width transition always runs.
  useEffect(() => {
    let raf1 = null;
    let raf2 = null;
    const timer = setTimeout(() => {
      raf1 = requestAnimationFrame(() => {
        raf2 = requestAnimationFrame(() => setIsAnimated(true));
      });
    }, Math.max(0, startDelay) + index * 110);
    return () => {
      clearTimeout(timer);
      if (raf1) cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
    };
  }, [index, startDelay]);
  
  const barColor = getSeverityColor(severity === 'none' ? 'none' : 
                   dropoffRate >= 20 ? 'critical' :
                   dropoffRate >= 10 ? 'high' :
                   dropoffRate >= 5 ? 'moderate' : 'low');
  
  // Gradient colors based on severity
  const gradientId = `funnel-gradient-${index}`;
  const gradientEndColor = dropoffRate >= 20 ? '#991B1B' :
                           dropoffRate >= 10 ? '#B45309' :
                           dropoffRate >= 5 ? '#D97706' : '#059669';
  
  return (
    <div 
      style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '16px', 
        marginBottom: '8px',
        opacity: isAnimated ? 1 : 0,
        transform: isAnimated ? 'translateX(0)' : 'translateX(-20px)',
        transition: 'all 0.5s ease-out'
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div style={{ 
        width: '140px', 
        fontSize: '13px', 
        fontWeight: isHovered ? '600' : '500',
        color: isHovered ? colors.primary : colors.text,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        transition: 'all 0.2s ease'
      }} title={variable}>
        {variable}
      </div>
      <div style={{ flex: 1, position: 'relative' }}>
        {/* SVG for gradient definition */}
        <svg width="0" height="0" style={{ position: 'absolute' }}>
          <defs>
            <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor={barColor} />
              <stop offset="100%" stopColor={gradientEndColor} />
            </linearGradient>
          </defs>
        </svg>
        <div style={{
          height: '36px',
          backgroundColor: isDarkMode ? '#1F1F23' : '#E5E7EB',
          borderRadius: '8px',
          overflow: 'hidden',
          position: 'relative',
          boxShadow: isHovered ? `0 0 0 2px ${barColor}40` : 'none',
          transition: 'box-shadow 0.2s ease'
        }}>
          <div style={{
            height: '100%',
            width: isAnimated ? `${percentage}%` : '0%',
            background: `linear-gradient(90deg, ${barColor}, ${gradientEndColor})`,
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            paddingRight: '12px',
            transition: 'width 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
            position: 'relative',
            overflow: 'hidden'
          }}>
            {/* Animated shine effect */}
            {isHovered && (
              <div style={{
                position: 'absolute',
                top: 0,
                left: '-100%',
                width: '100%',
                height: '100%',
                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
                animation: 'shine 1s ease-in-out'
              }} />
            )}
            {percentage > 15 && (
              <span style={{ 
                color: 'white', 
                fontSize: '13px', 
                fontWeight: '600',
                textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                zIndex: 1
              }}>
                {responses.toLocaleString()}
              </span>
            )}
          </div>
        </div>
        
        {/* Tooltip on hover */}
        {isHovered && (
          <div style={{
            position: 'absolute',
            top: '-45px',
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: isDarkMode ? '#2A2A2E' : '#1F2937',
            color: 'white',
            padding: '8px 12px',
            borderRadius: '8px',
            fontSize: '12px',
            whiteSpace: 'nowrap',
            zIndex: 10,
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            animation: 'fadeIn 0.2s ease'
          }}>
            <strong>{responses.toLocaleString()}</strong> of {total.toLocaleString()} responses ({percentage.toFixed(1)}%)
            <div style={{
              position: 'absolute',
              bottom: '-6px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: 0,
              height: 0,
              borderLeft: '6px solid transparent',
              borderRight: '6px solid transparent',
              borderTop: `6px solid ${isDarkMode ? '#2A2A2E' : '#1F2937'}`
            }} />
          </div>
        )}
      </div>
      <div style={{ width: '90px', textAlign: 'right' }}>
        <div style={{ 
          fontSize: '15px', 
          fontWeight: '700', 
          color: isHovered ? colors.primary : colors.text,
          transition: 'color 0.2s ease'
        }}>
          {percentage.toFixed(1)}%
        </div>
        {!isLast && dropoffRate > 0 && (
          <div style={{ 
            fontSize: '12px', 
            color: barColor, 
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: '2px'
          }}>
            <TrendingDown size={12} />
            {dropoffRate.toFixed(1)}%
          </div>
        )}
      </div>
      
      {/* Drop-off connector arrow */}
      {!isLast && dropoffRate > 0 && (
        <div style={{
          position: 'absolute',
          right: '-25px',
          top: '50%',
          transform: 'translateY(-50%)',
          color: barColor,
          opacity: isHovered ? 1 : 0.5,
          transition: 'opacity 0.2s ease'
        }}>
          <ChevronDown size={16} />
        </div>
      )}
    </div>
  );
};

const SurveyFlowDashboard = ({ data, dataCollectorId, isSidePanelExpanded, xlsformData, isMobile = false }) => {
  const { isDarkMode } = useTheme();
  
  // Inject animation styles
  useEffect(() => {
    const styleId = 'survey-flow-animations';
    if (!document.getElementById(styleId)) {
      const styleSheet = document.createElement('style');
      styleSheet.id = styleId;
      styleSheet.textContent = animationStyles;
      document.head.appendChild(styleSheet);
    }
    return () => {
      const existing = document.getElementById(styleId);
      if (existing) existing.remove();
    };
  }, []);
  
  // Tab state
  const [activeTab, setActiveTab] = useState('overview');
  
  // Sankey filter states
  const [sankeyMinCount, setSankeyMinCount] = useState(5);
  const [highlightedPath, setHighlightedPath] = useState(null);
  
  // View states
  const [funnelView, setFunnelView] = useState('chart');
  const [collectorView, setCollectorView] = useState('table');
  const [collectorSortField, setCollectorSortField] = useState('complete_response_rate');
  const [collectorSortDirection, setCollectorSortDirection] = useState('desc');
  
  // Skip Logic expansion state
  const [expandedRules, setExpandedRules] = useState({});
  const [skipLogicSortField, setSkipLogicSortField] = useState('total_violations');
  const [skipLogicSortDirection, setSkipLogicSortDirection] = useState('desc');
 
  // Memoized sorted collectors - MUST be at top level, not inside render function
  const sortedCollectors = useMemo(() => {
    const byCollector = data?.overall?.by_collector || {};
    return Object.entries(byCollector)
      .map(([id, collectorData]) => ({ id, ...collectorData }))
      .sort((a, b) => {
        const aVal = a[collectorSortField] ?? 0;
        const bVal = b[collectorSortField] ?? 0;
        return collectorSortDirection === 'desc' ? bVal - aVal : aVal - bVal;
      });
  }, [data?.overall?.by_collector, collectorSortField, collectorSortDirection]);
 
  // Color scheme
  const colors = {
    background: isDarkMode ? '#0D0E0E' : '#F5F5F5',
    cardBg: isDarkMode ? '#1a1a1dff' : '#FFFFFF',
    cardBgAlt: isDarkMode ? '#1a1a1dff' : '#FAFAFA',
    border: isDarkMode ? '#1F1F23' : '#E5E7EB',
    borderLight: isDarkMode ? '#2A2A2E' : '#F3F4F6',
    text: isDarkMode ? '#929292ff' : '#1F2937',
    textSecondary: isDarkMode ? '#9CA3AF' : '#6B7280',
    textMuted: isDarkMode ? '#6B7280' : '#9CA3AF',
    primary: isDarkMode ? '#5f5f5f' : '#a8a8a8',
    success: '#10B981',
    warning: '#F59E0B',
    error: '#EF4444',
    purple: '#8B5CF6',
    cyan: '#06B6D4',
    neutral: '#6B7280',
  };
 
  // Early return if no data
  if (!data?.overall) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px',
        padding: '32px'
      }}>
        <div className="nsx-metric-card" style={{
          textAlign: 'center',
          padding: '32px',
          borderRadius: '16px',
          backgroundColor: colors.cardBg,
          border: `1px solid ${colors.border}`
        }}>
          <GitBranch size={48} style={{ color: colors.textMuted, marginBottom: '12px' }} />
          <h3 style={{ fontSize: '16px', fontWeight: '600', color: colors.text, marginBottom: '8px' }}>
            No Survey Flow Data Available
          </h3>
          <p style={{ fontSize: '13px', color: colors.textSecondary }}>
            Configure and run a survey flow analysis to see the results here.
          </p>
        </div>
      </div>
    );
  }
 
  const overall = data.overall;
  const hasCollectorData = overall.by_collector && Object.keys(overall.by_collector).length > 0;
  const collectors = hasCollectorData ? Object.keys(overall.by_collector).sort() : [];
 
  // Get quality score data
  const qualityScore = overall.quality_score || { overall_score: 0, component_scores: {}, interpretation: {} };
  const pathAnalysis = overall.path_analysis || {};
  const dropoffAnalysis = overall.dropoff_analysis || {};
  const patterns = overall.patterns || {};
  const transitionQuality = overall.transition_quality || {};
  const diversityMetrics = overall.diversity_metrics || {};
  const collectorComparison = overall.collector_comparison || {};
 
  // Utility functions
  const getRatingColor = (rating) => {
    const ratingColors = {
      'Excellent': '#10B981',
      'Good': '#34D399',
      'Moderate': '#F59E0B',
      'Poor': '#F97316',
      'Critical': '#EF4444'
    };
    return ratingColors[rating] || colors.textMuted;
  };
 
  const getSeverityColor = (severity) => {
    const severityColors = {
      'critical': '#EF4444',
      'high': '#F97316',
      'moderate': '#F59E0B',
      'low': '#34D399',
      'none': '#10B981'
    };
    return severityColors[severity] || colors.textMuted;
  };
 
  // Dynamic color based on completion/rate percentage (green→yellow→red spectrum)
  const getCompletionColor = (rate) => {
    if (rate >= 90) return '#10B981';
    if (rate >= 75) return '#34D399';
    if (rate >= 60) return '#F59E0B';
    if (rate >= 40) return '#F97316';
    return '#EF4444';
  };
 
  // Tab definitions
  // Skip Logic tab visibility: show only if XLSForm loaded AND has skip logic rules AND has matched variables
  const skipLogicAnalysis = data?.overall?.skip_logic_analysis;
  const hasSkipLogicData = skipLogicAnalysis && 
    skipLogicAnalysis.has_rules && 
    skipLogicAnalysis.statistics?.rules_evaluated > 0;
  
  const tabs = [
    { id: 'overview', label: 'Flow Overview', icon: BarChart3 },
    { id: 'paths', label: 'Path Explorer', icon: GitBranch },
    { id: 'dropoff', label: 'Drop-off Analysis', icon: TrendingDown },
    ...(hasCollectorData ? [{ id: 'collectors', label: 'Collector Analysis', icon: Users }] : []),
    ...(hasSkipLogicData ? [{ id: 'skiplogic', label: 'Skip Logic', icon: Shield }] : [])
  ];
 
  // ============== REUSABLE COMPONENTS ==============
 
 
 
  const PatternAlert = ({ icon: Icon, title, description, severity }) => (
    <div style={{
      padding: '14px 18px',
      backgroundColor: severity === 'critical' 
        ? (isDarkMode ? '#1F1116' : '#FEF2F2')
        : severity === 'warning'
        ? (isDarkMode ? '#1C1917' : '#FFFBEB')
        : severity === 'success'
        ? (isDarkMode ? '#0F2922' : '#F0FDF4')
        : (isDarkMode ? '#0F172A' : '#F0F9FF'),
      borderRadius: '10px',
      border: `1px solid ${
        severity === 'critical' ? '#EF4444' : 
        severity === 'warning' ? '#F59E0B' : 
        severity === 'success' ? '#10B981' : '#a8a8a8'
      }30`
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
        <Icon size={18} style={{ 
          color: severity === 'critical' ? '#EF4444' : 
                 severity === 'warning' ? '#F59E0B' : 
                 severity === 'success' ? '#10B981' : '#a8a8a8',
          marginTop: '2px'
        }} />
        <div>
          <div style={{ 
            fontSize: '15px', 
            fontWeight: '500', 
            color: severity === 'critical' ? '#EF4444' : 
                   severity === 'warning' ? '#F59E0B' : 
                   severity === 'success' ? '#10B981' : '#a8a8a8',
            marginBottom: '4px'
          }}>
            {title}
          </div>
          <div style={{ fontSize: '13px', color: colors.textSecondary }}>
            {description}
          </div>
        </div>
      </div>
    </div>
  );
 
  const InsightCard = ({ type, title, description }) => {
    const typeConfig = {
      success: { bg: isDarkMode ? '#0F2922' : '#F0FDF4', border: '#10B981', icon: CheckCircle },
      warning: { bg: isDarkMode ? '#1C1917' : '#FFFBEB', border: '#F59E0B', icon: AlertTriangle },
      info: { bg: isDarkMode ? '#0F172A' : '#F0F9FF', border: '#a8a8a8', icon: Info }
    };
    const config = typeConfig[type] || typeConfig.info;
    const IconComponent = config.icon;
    
    return (
      <div style={{
        padding: '12px 16px',
        backgroundColor: config.bg,
        borderRadius: '12px',
        border: `1px solid ${config.border}30`
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
          <IconComponent size={20} style={{ color: config.border, marginTop: '2px', flexShrink: 0 }} />
          <div>
            <div style={{ 
              fontSize: '15px', 
              fontWeight: '600', 
              color: config.border,
              marginBottom: '6px'
            }}>
              {title}
            </div>
            <div style={{ fontSize: '13px', color: colors.textSecondary, lineHeight: '1.5' }}>
              {description}
            </div>
          </div>
        </div>
      </div>
    );
  };
 
 
  // Sortable header for tables
  const SortableHeader = ({ field, label, align = 'left' }) => {
    const isActive = collectorSortField === field;
    return (
      <th 
        onClick={() => {
          if (collectorSortField === field) {
            setCollectorSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
          } else {
            setCollectorSortField(field);
            setCollectorSortDirection('desc');
          }
        }}
        style={{ 
          padding: '14px 16px', 
          textAlign: align, 
          fontSize: '14px', 
          fontWeight: '450',
          color: isActive ? colors.primary : colors.textSecondary,
          cursor: 'pointer',
          userSelect: 'none',
          whiteSpace: 'nowrap',
          borderBottom: `2px solid ${colors.border}`
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', justifyContent: align === 'center' ? 'center' : 'flex-start' }}>
          {label}
          <ArrowUpDown size={14} style={{ opacity: isActive ? 1 : 0.4 }} />
        </div>
      </th>
    );
  };
 
  // ============== OVERVIEW TAB ==============
  const renderOverviewTab = () => {
    const summary = overall.summary || {};
    const interpretation = qualityScore.interpretation || {};
    
    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: '16px', marginTop: '80px', overflow: 'hidden', width: '100%' }}>
        
        <CollapsibleSection 
          title="Key Metrics" 
          isDarkMode={isDarkMode} 
          colors={colors}
          badge={
            (qualityScore.overall_score || 0) < 70 ? (
              <span style={{ padding: '2px 8px', borderRadius: '10px', backgroundColor: '#EF444420', color: '#EF4444', fontSize: '11px', fontWeight: '600' }}>
                needs review
              </span>
            ) : null
          }
        >
        {/* Six metric cards, same card language as the other detectors */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? 'repeat(2, minmax(0, 1fr))' : 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: '16px',
        }}>
          {(() => {
            const rate = summary.overall_completion_rate || 0;
            const completePct = pathAnalysis.complete_response_rate || 0;
            const drop = dropoffAnalysis.biggest_dropoff;
            const bottlenecks = transitionQuality.total_bottlenecks || 0;
            return (
              <>
                <MetricCard
                  colors={colors}
                  isDarkMode={isDarkMode}
                  isMobile={isMobile}
                  animDelay={0}
                  icon={Target}
                  title="Completion Rate"
                  value={`${rate.toFixed(1)}%`}
                  subtitle="First to last variable"
                  color={getCompletionColor(rate)}
                  warning={rate > 100 ? 'Rate exceeds 100%. This usually means skip logic or later variables answered more often than the first.' : undefined}
                  severity={rate < 60 ? 'critical' : rate < 75 ? 'warning' : null}
                />
                <MetricCard
                  colors={colors}
                  isDarkMode={isDarkMode}
                  isMobile={isMobile}
                  animDelay={80}
                  icon={Activity}
                  title="Complete Responses"
                  value={(pathAnalysis.total_complete_responses || 0).toLocaleString()}
                  subtitle={`${completePct.toFixed(1)}% of ${(overall.total_responses || 0).toLocaleString()} responses`}
                  color={getCompletionColor(completePct)}
                  severity={completePct < 60 ? 'critical' : completePct < 75 ? 'warning' : null}
                />
                <MetricCard
                  colors={colors}
                  isDarkMode={isDarkMode}
                  isMobile={isMobile}
                  animDelay={160}
                  icon={Layers}
                  title="Variables Analyzed"
                  value={summary.variables_analyzed || 0}
                  subtitle="Across the response path"
                  color={colors.neutral}
                />
                <MetricCard
                  colors={colors}
                  isDarkMode={isDarkMode}
                  isMobile={isMobile}
                  animDelay={240}
                  icon={GitBranch}
                  title="Unique Paths"
                  value={(pathAnalysis.unique_paths || 0).toLocaleString()}
                  subtitle={`Diversity ${(pathAnalysis.path_diversity_index || 0).toFixed(0)}%`}
                  color={colors.neutral}
                  onClick={() => setActiveTab('paths')}
                />
                <MetricCard
                  colors={colors}
                  isDarkMode={isDarkMode}
                  isMobile={isMobile}
                  animDelay={320}
                  icon={TrendingDown}
                  title="Biggest Drop-off"
                  value={drop ? `${Number(drop.rate).toFixed(1)}%` : 'None'}
                  subtitle={drop?.variable || 'No significant drops'}
                  color={drop && drop.rate > 15 ? colors.error : colors.warning}
                  severity={drop?.rate > 25 ? 'critical' : drop?.rate > 15 ? 'warning' : null}
                  onClick={() => setActiveTab('dropoff')}
                />
                <MetricCard
                  colors={colors}
                  isDarkMode={isDarkMode}
                  isMobile={isMobile}
                  animDelay={400}
                  icon={Zap}
                  title="Flow Bottlenecks"
                  value={bottlenecks}
                  subtitle={`Avg diversity ${(transitionQuality.average_diversity || 0).toFixed(0)}%`}
                  color={bottlenecks > 0 ? colors.warning : colors.success}
                  severity={bottlenecks > 3 ? 'critical' : bottlenecks > 0 ? 'warning' : null}
                />
              </>
            );
          })()}
        </div>
        </CollapsibleSection>

        <CollapsibleSection title="Analysis & Issues" isDarkMode={isDarkMode} colors={colors}>
        {/* Score ring | score breakdown | insights (equal heights via .analysis-grid-4col) */}
        <div className="analysis-grid-4col sf-analysis-grid" style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? 'minmax(0, 1fr)' : 'minmax(0, 1fr) minmax(0, 1.15fr) minmax(0, 1.85fr)',
          gap: '16px',
          marginBottom: '16px',
        }}>
          {/* Flow quality score */}
          <AnimatedCard delay={480} translateY={12}>
            {(() => {
              const score = qualityScore.overall_score || 0;
              const rating = interpretation.rating || 'N/A';
              const captionByRating = {
                Excellent: 'Healthy response flow',
                Good: 'Minor areas to improve',
                Moderate: 'Review drop-offs',
                Poor: 'Flow needs attention',
                Critical: 'Flow is breaking down',
              };
              return (
                <ScoreCard
                  eyebrow="flow quality score"
                  score={score}
                  label={rating}
                  caption={captionByRating[rating] || 'Completion, retention and consistency'}
                  color={interpretation.color || getRatingColor(rating)}
                  severity={score < 60 ? 'critical' : score < 80 ? 'warning' : null}
                  isDarkMode={isDarkMode}
                  startDelay={780}
                />
              );
            })()}
          </AnimatedCard>

          {/* Score breakdown */}
          <AnimatedCard delay={560} translateY={12}>
            <div className="nsx-metric-card" style={{
              position: 'relative',
              padding: '18px',
              backgroundColor: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.6)',
              borderRadius: '10px',
              border: `0.5px solid ${isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              overflow: 'hidden',
              fontFamily: "'Outfit', -apple-system, sans-serif",
            }}>
              <div style={{ fontSize: '10px', fontWeight: 500, letterSpacing: '0.16em', textTransform: 'lowercase', color: colors.textMuted, marginBottom: '3px' }}>
                score breakdown
              </div>
              <div style={{ fontSize: '13.5px', fontWeight: 500, letterSpacing: '-0.01em', color: colors.text, marginBottom: '14px' }}>
                What drives the score
              </div>
              {Object.entries(qualityScore.component_scores || {}).map(([key, value], idx, arr) => {
                const v = Number(value) || 0;
                const c = v >= 80 ? (isDarkMode ? '#34d399' : '#047857') : v >= 60 ? '#D97706' : (isDarkMode ? '#f87171' : '#b91c1c');
                return (
                  <div key={key} style={{
                    padding: '8px 0',
                    borderBottom: idx < arr.length - 1 ? `0.5px solid ${isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}` : 'none',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '6px' }}>
                      <span style={{ fontSize: '12px', color: colors.text, textTransform: 'capitalize', letterSpacing: '-0.005em' }}>
                        {key.replace(/_/g, ' ')}
                      </span>
                      <span style={{ fontSize: '13px', fontWeight: 500, color: c, fontVariantNumeric: 'tabular-nums' }}>
                        {v.toFixed(0)}
                      </span>
                    </div>
                    <div style={{ height: '5px', borderRadius: '3px', background: isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)', overflow: 'hidden' }}>
                      <div style={{
                        width: `${Math.max(0, Math.min(100, v))}%`,
                        height: '100%',
                        borderRadius: '3px',
                        background: c,
                        transformOrigin: 'left',
                        animation: `sfBarGrow 900ms cubic-bezier(0.4, 0, 0.2, 1) ${820 + idx * 90}ms both`,
                      }} />
                    </div>
                  </div>
                );
              })}
              {Object.keys(qualityScore.component_scores || {}).length === 0 && (
                <div style={{ padding: '22px 0', textAlign: 'center', color: colors.textMuted, fontSize: '11.5px' }}>No component scores available</div>
              )}
            </div>
          </AnimatedCard>

          {/* Insights & alerts */}
          <AnimatedCard delay={640} translateY={12}>
            {(() => {
              const drop = dropoffAnalysis.biggest_dropoff;
              const items = [];
              if (drop && drop.rate >= 15) items.push({ tone: 'critical', Icon: AlertTriangle, title: 'Critical drop-off detected', text: `${Number(drop.rate).toFixed(1)}% drop at "${drop.variable}". Review question complexity.` });
              if ((summary.overall_completion_rate || 100) < 70) items.push({ tone: 'warning', Icon: TrendingDown, title: 'Low completion rate', text: `Only ${(summary.overall_completion_rate || 0).toFixed(1)}% complete the survey. Consider shortening or restructuring.` });
              if (patterns.straight_lining && patterns.straight_lining.percentage > 5) items.push({ tone: 'warning', Icon: RefreshCw, title: 'Potential straight-lining', text: `${patterns.straight_lining.percentage.toFixed(1)}% of respondents show repetitive answer patterns.` });
              if (pathAnalysis.most_common_path) items.push({ tone: 'success', Icon: CheckCircle, title: 'Most common path', text: `${pathAnalysis.most_common_path.count} responses (${pathAnalysis.most_common_path.percentage.toFixed(1)}%) follow the same path.` });
              if ((qualityScore.overall_score || 0) >= 85) items.push({ tone: 'success', Icon: CheckCircle, title: 'Healthy survey flow', text: 'High completion rates and consistent response patterns.' });
              const toneColor = (t) => t === 'critical' ? (isDarkMode ? '#f87171' : '#b91c1c') : t === 'warning' ? (isDarkMode ? '#fbbf24' : '#b45309') : (isDarkMode ? '#34d399' : '#047857');
              return (
                <div className="nsx-metric-card" style={{
                  position: 'relative',
                  padding: '18px',
                  backgroundColor: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.6)',
                  borderRadius: '10px',
                  border: `0.5px solid ${isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
                  backdropFilter: 'blur(20px)',
                  WebkitBackdropFilter: 'blur(20px)',
                  overflow: 'hidden',
                  fontFamily: "'Outfit', -apple-system, sans-serif",
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px', gap: '10px' }}>
                    <div>
                      <div style={{ fontSize: '10px', fontWeight: 500, letterSpacing: '0.16em', textTransform: 'lowercase', color: colors.textMuted, marginBottom: '3px' }}>flow insights</div>
                      <div style={{ fontSize: '13.5px', fontWeight: 500, letterSpacing: '-0.01em', color: colors.text }}>Insights & Alerts</div>
                    </div>
                    <span style={{ fontSize: '9.5px', fontWeight: 500, letterSpacing: '0.06em', textTransform: 'lowercase', color: colors.textMuted, paddingTop: '14px' }}>{items.length} item{items.length === 1 ? '' : 's'}</span>
                  </div>
                  {items.map((it, i) => {
                    const c = toneColor(it.tone);
                    return (
                      <div key={i} style={{
                        display: 'flex', alignItems: 'flex-start', gap: '12px',
                        padding: '10px 0',
                        borderBottom: i < items.length - 1 ? `0.5px solid ${isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}` : 'none',
                      }}>
                        <div style={{
                          width: '28px', height: '28px', borderRadius: '8px', flexShrink: 0,
                          background: `${c}${isDarkMode ? '22' : '14'}`, color: c,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          <it.Icon size={14} strokeWidth={2} />
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: '12.5px', fontWeight: 500, color: colors.text, letterSpacing: '-0.005em', marginBottom: '2px' }}>{it.title}</div>
                          <div style={{ fontSize: '11.5px', color: colors.textMuted, lineHeight: 1.45, wordBreak: 'break-word' }}>{it.text}</div>
                        </div>
                      </div>
                    );
                  })}
                  {items.length === 0 && (
                    <div style={{ padding: '22px 0', textAlign: 'center', color: colors.textMuted, fontSize: '11.5px' }}>No alerts for this survey flow</div>
                  )}
                </div>
              );
            })()}
          </AnimatedCard>
        </div>

        <style>{`
          @media (max-width: 1200px) { .sf-analysis-grid { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; } }
          @media (max-width: 768px) { .sf-analysis-grid { grid-template-columns: minmax(0, 1fr) !important; } }
        `}</style>

        {/* Mini Completion Funnel */}
        <AnimatedCard delay={720} translateY={12}>
        <div className="nsx-metric-card" style={{
          padding: '18px',
          backgroundColor: colors.cardBg,
          borderRadius: '12px',
          border: `1px solid ${colors.border}`
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3 style={{ fontSize: '13px', fontWeight: '500', color: colors.text, margin: 0 }}>
              Completion Funnel Preview
            </h3>
            <button
              onClick={() => setActiveTab('dropoff')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 14px',
                borderRadius: '8px',
                border: `1px solid ${colors.border}`,
                backgroundColor: 'transparent',
                color: colors.primary,
                fontSize: '13px',
                cursor: 'pointer'
              }}
            >
              View Details <ArrowRight size={14} />
            </button>
          </div>
          
          {dropoffAnalysis.by_variable && dropoffAnalysis.by_variable.slice(0, 6).map((item, idx) => (
            <FunnelBar
              key={item.variable}
              colors={colors}
              isDarkMode={isDarkMode}
              getSeverityColor={getSeverityColor}
              startDelay={1000}
              variable={item.variable}
              responses={item.responses}
              total={overall.total_responses || 1}
              dropoffRate={item.dropoff_rate}
              severity={item.severity}
              isLast={idx === Math.min(5, dropoffAnalysis.by_variable.length - 1)}
              index={idx}
            />
          ))}
          
          {dropoffAnalysis.by_variable && dropoffAnalysis.by_variable.length > 6 && (
            <div style={{ textAlign: 'center', padding: '12px', color: colors.textMuted, fontSize: '13px' }}>
              +{dropoffAnalysis.by_variable.length - 6} more variables
            </div>
          )}
        </div>
        </AnimatedCard>
        </CollapsibleSection>
      </div>
    );
  };
 
  // ============== PATH EXPLORER TAB ==============
  const renderPathExplorerTab = () => {
    const topPaths = pathAnalysis.top_paths || [];
    const pathConcentration = pathAnalysis.path_concentration || {};
    
    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: '16px', marginTop: '80px', overflow: 'hidden', width: '100%' }}>
        {/* Path Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'minmax(0, 1fr)' : 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))', gap: '16px', minWidth: 0 }}>
          <MetricCard
            colors={colors}
            isDarkMode={isDarkMode}
            isMobile={isMobile}
            animDelay={0}
            icon={Activity}
            title="Complete Responses"
            value={(pathAnalysis.total_complete_responses || 0).toLocaleString()}
            subtitle={`${(pathAnalysis.complete_response_rate || 0).toFixed(1)}% completion rate`}
            color={getCompletionColor(pathAnalysis.complete_response_rate || 0)}
          />
          <MetricCard
            colors={colors}
            isDarkMode={isDarkMode}
            isMobile={isMobile}
            animDelay={80}
            icon={GitBranch}
            title="Unique Paths"
            value={pathAnalysis.unique_paths || 0}
            subtitle="Different response combinations"
            color={colors.neutral}
          />
          <MetricCard
            colors={colors}
            isDarkMode={isDarkMode}
            isMobile={isMobile}
            animDelay={160}
            icon={Target}
            title="Path Diversity"
            value={`${(pathAnalysis.path_diversity_index || 0).toFixed(0)}%`}
            subtitle="Higher = more varied responses"
            color={colors.neutral}
          />
          <MetricCard
            colors={colors}
            isDarkMode={isDarkMode}
            isMobile={isMobile}
            animDelay={240}
            icon={Layers}
            title="Top 5 Path Share"
            value={`${(pathConcentration.top_5_paths_share || 0).toFixed(1)}%`}
            subtitle="Response concentration"
            color={colors.neutral}
          />
        </div>
 
        {/* Sankey Diagram */}
        <AnimatedCard delay={0} translateY={12}>
        <div className="nsx-metric-card" style={{
          position: 'relative',
          padding: isMobile ? '16px 8px' : '24px',
          backgroundColor: colors.cardBg,
          borderRadius: '12px',
          border: `1px solid ${colors.border}`,
          overflow: 'hidden',
          minWidth: 0
        }}>
          <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', gap: isMobile ? '12px' : '0', marginBottom: '20px' }}>
            <div>
              <h3 style={{ fontSize: '13px', fontWeight: '500', color: colors.text, margin: 0 }}>
                Response Flow Visualization
              </h3>
              <p style={{ fontSize: '15px', color: colors.textMuted, marginTop: '4px' }}>
                {isMobile ? 'Swipe horizontally to explore the full flow' : 'Hover over paths and nodes to explore transitions'}
              </p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <label style={{ fontSize: '13px', color: colors.textSecondary }}>Min count:</label>
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={sankeyMinCount}
                  onChange={(e) => setSankeyMinCount(Math.max(1, parseInt(e.target.value) || 1))}
                  style={{
                    width: '60px',
                    padding: '6px 10px',
                    borderRadius: '6px',
                    border: `1px solid ${colors.border}`,
                    backgroundColor: colors.cardBgAlt,
                    color: colors.text,
                    fontSize: '13px'
                  }}
                />
              </div>
            </div>
          </div>
 
          {/* ============== SANKEY SCROLL CONTAINER ==============
              This is the one and only scroll container for the Sankey.
              The Sankey's SVG has minWidth: 500/800px. When the viewport
              is narrower than that, THIS div scrolls horizontally. The
              internal wrapper inside SankeyDiagram was redundant and being
              clipped by ancestor overflow:hidden, so we scroll at the
              SankeyDiagram's sibling level instead.
          */}
          <div
            style={{
              overflowX: 'auto',
              overflowY: 'hidden',
              WebkitOverflowScrolling: 'touch',
              touchAction: 'pan-x pan-y',
              // Give horizontal scrollbar some breathing room
              paddingBottom: '4px',
              // Negative margin breaks out of the card's padding so the scroll
              // area spans edge-to-edge. Padding is restored inside.
              margin: isMobile ? '0 -8px' : '0 -24px',
              paddingLeft: isMobile ? '8px' : '24px',
              paddingRight: isMobile ? '8px' : '24px',
            }}
          >
            <SankeyDiagram
              data={overall}
              minCount={sankeyMinCount}
              isDarkMode={isDarkMode}
              colors={colors}
              isMobile={isMobile}
            />
          </div>
 
          {/* Swipe-hint fade on the right edge — only on mobile. */}
          {isMobile && (
            <div
              aria-hidden="true"
              style={{
                position: 'absolute',
                right: 0,
                top: 60,
                bottom: 8,
                width: 32,
                pointerEvents: 'none',
                background: `linear-gradient(to right, transparent, ${colors.cardBg} 80%)`,
                borderRadius: '0 12px 12px 0',
              }}
            />
          )}
        </div>
        </AnimatedCard>
 
        {/* Top Paths Table */}
        <AnimatedCard delay={80} translateY={12}>
        <div className="nsx-metric-card" style={{
          backgroundColor: colors.cardBg,
          borderRadius: '12px',
          border: `1px solid ${colors.border}`,
          overflow: 'hidden',
          minWidth: 0
        }}>
          <div style={{ padding: '16px', borderBottom: `1px solid ${colors.border}` }}>
            <h3 style={{ fontSize: '13px', fontWeight: '500', color: colors.text, margin: 0 }}>
              Top Response Paths
            </h3>
          </div>
          <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <table style={{ width: '100%', minWidth: isMobile ? '500px' : 'auto', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: colors.cardBgAlt }}>
                  <th style={{ padding: '14px 16px', textAlign: 'left', fontSize: '14px', fontWeight: '450', color: colors.textSecondary }}>Rank</th>
                  <th style={{ padding: '14px 16px', textAlign: 'left', fontSize: '14px', fontWeight: '450', color: colors.textSecondary }}>Path</th>
                  <th style={{ padding: '14px 16px', textAlign: 'center', fontSize: '14px', fontWeight: '450', color: colors.textSecondary }}>Count</th>
                  <th style={{ padding: '14px 16px', textAlign: 'center', fontSize: '14px', fontWeight: '450', color: colors.textSecondary }}>Percentage</th>
                </tr>
              </thead>
              <tbody>
                {topPaths.slice(0, 10).map((path, idx) => (
                  <tr key={idx} style={{ borderBottom: `1px solid ${colors.borderLight}` }}>
                    <td style={{ padding: '14px 16px', fontSize: '15px', fontWeight: '450', color: colors.neutral }}>
                      #{idx + 1}
                    </td>
                    <td style={{ padding: '14px 16px', fontSize: '15px', color: colors.text }}>
                      <div style={{ 
                        display: 'flex', 
                        flexWrap: 'wrap', 
                        gap: '6px',
                        alignItems: 'center'
                      }}>
                        {path.path.map((step, stepIdx) => (
                          <React.Fragment key={stepIdx}>
                            <span style={{
                              padding: '4px 10px',
                              backgroundColor: colors.cardBgAlt,
                              borderRadius: '6px',
                              fontSize: '15px',
                              fontWeight: '450'
                            }}>
                              {step}
                            </span>
                            {stepIdx < path.path.length - 1 && (
                              <ArrowRight size={14} style={{ color: colors.textMuted }} />
                            )}
                          </React.Fragment>
                        ))}
                      </div>
                    </td>
                    <td style={{ padding: '14px 16px', textAlign: 'center', fontSize: '14px', fontWeight: '600', color: colors.text }}>
                      {path.count}
                    </td>
                    <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                      <div style={{
                        display: 'inline-block',
                        padding: '4px 12px',
                        borderRadius: '12px',
                        backgroundColor: `${colors.neutral}15`,
                        color: colors.neutral,
                        fontSize: '13px',
                        fontWeight: '600'
                      }}>
                        {path.percentage.toFixed(1)}%
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        </AnimatedCard>
      </div>
    );
  };
 
  // ============== DROP-OFF ANALYSIS TAB ==============
  const renderDropoffTab = () => {
    const byVariable = dropoffAnalysis.by_variable || [];
    const criticalPoints = dropoffAnalysis.critical_points || [];
    
    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: '16px', marginTop: '80px', overflow: 'hidden', width: '100%' }}>
        {/* Summary Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'minmax(0, 1fr)' : 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))', gap: '16px' }}>
          <MetricCard
            colors={colors}
            isDarkMode={isDarkMode}
            isMobile={isMobile}
            animDelay={0}
            icon={TrendingDown}
            title="Total Drop-offs"
            value={(dropoffAnalysis.total_dropoffs || 0).toLocaleString()}
            subtitle="Cumulative across all variables"
            color={colors.error}
            severity={(dropoffAnalysis.total_dropoffs || 0) > 0 ? 'warning' : null}
          />
          <MetricCard
            colors={colors}
            isDarkMode={isDarkMode}
            isMobile={isMobile}
            animDelay={80}
            icon={Target}
            title="Final Retention"
            value={`${(dropoffAnalysis.final_retention_rate || 0).toFixed(1)}%`}
            subtitle="Respondents reaching final variable"
            color={colors.success}
            severity={(dropoffAnalysis.final_retention_rate || 0) < 70 ? 'critical' : (dropoffAnalysis.final_retention_rate || 0) < 85 ? 'warning' : null}
          />
          <MetricCard
            colors={colors}
            isDarkMode={isDarkMode}
            isMobile={isMobile}
            animDelay={160}
            icon={AlertTriangle}
            title="Critical Drop Points"
            value={criticalPoints.length}
            subtitle="Variables with >10% drop-off"
            color={criticalPoints.length > 0 ? colors.error : colors.success}
            severity={criticalPoints.length > 3 ? 'critical' : criticalPoints.length > 0 ? 'warning' : null}
          />
          <MetricCard
            colors={colors}
            isDarkMode={isDarkMode}
            isMobile={isMobile}
            animDelay={240}
            icon={Activity}
            title="Avg Drop-off Rate"
            value={`${(dropoffAnalysis.average_dropoff_rate || 0).toFixed(1)}%`}
            subtitle="Per variable average"
            color={colors.warning}
            severity={(dropoffAnalysis.average_dropoff_rate || 0) > 10 ? 'critical' : (dropoffAnalysis.average_dropoff_rate || 0) > 5 ? 'warning' : null}
          />
        </div>
 
        {/* View Toggle */}
        <AnimatedCard delay={0} translateY={12}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h3 style={{ fontSize: '13px', fontWeight: '500', color: colors.text, margin: 0 }}>
            Drop-off by Variable
          </h3>
          <div className="nsx-metric-card" style={{
            display: 'flex',
            backgroundColor: colors.cardBg,
            borderRadius: '10px',
            border: `1px solid ${colors.border}`,
            overflow: 'hidden'
          }}>
            <button
              onClick={() => setFunnelView('chart')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '10px 16px',
                border: 'none',
                backgroundColor: funnelView === 'chart' ? colors.primary : 'transparent',
                color: funnelView === 'chart' ? 'white' : colors.textSecondary,
                fontSize: '13px',
                fontWeight: '500',
                cursor: 'pointer'
              }}
            >
              <BarChart3 size={16} />
              Funnel
            </button>
            <button
              onClick={() => setFunnelView('table')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '10px 16px',
                border: 'none',
                backgroundColor: funnelView === 'table' ? colors.primary : 'transparent',
                color: funnelView === 'table' ? 'white' : colors.textSecondary,
                fontSize: '13px',
                fontWeight: '500',
                cursor: 'pointer'
              }}
            >
              <Table size={16} />
              Table
            </button>
          </div>
        </div>
        </AnimatedCard>
 
        {/* Funnel Chart View */}
        {funnelView === 'chart' && (
          <div className="nsx-metric-card" style={{
            padding: '18px',
            backgroundColor: colors.cardBg,
            borderRadius: '12px',
            border: `1px solid ${colors.border}`
          }}>
            {byVariable.map((item, idx) => (
              <FunnelBar
                colors={colors}
                isDarkMode={isDarkMode}
                getSeverityColor={getSeverityColor}
                startDelay={300}
                key={item.variable}
                variable={item.variable}
                responses={item.responses}
                total={overall.total_responses || 1}
                dropoffRate={item.dropoff_rate}
                severity={item.severity}
                isLast={idx === byVariable.length - 1}
                index={idx}
              />
            ))}
 
            {/* Legend */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '20px',
              marginTop: '24px',
              paddingTop: '16px',
              borderTop: `1px solid ${colors.border}`
            }}>
              {[
                { label: 'No drop', color: '#10B981' },
                { label: 'Low (<5%)', color: '#34D399' },
                { label: 'Moderate (5-10%)', color: '#F59E0B' },
                { label: 'High (10-20%)', color: '#F97316' },
                { label: 'Critical (>20%)', color: '#EF4444' }
              ].map(item => (
                <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <div style={{ width: '12px', height: '12px', borderRadius: '3px', backgroundColor: item.color }} />
                  <span style={{ fontSize: '13px', color: colors.textSecondary }}>{item.label}</span>
                </div>
              ))}
            </div>
          </div>
        )}
 
        {/* Table View */}
        {funnelView === 'table' && (
          <div className="nsx-metric-card" style={{
            backgroundColor: colors.cardBg,
            borderRadius: '12px',
            border: `1px solid ${colors.border}`,
            overflow: 'hidden'
          }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: colors.cardBgAlt }}>
                    <th style={{ padding: '14px 16px', textAlign: 'left', fontSize: '14px', fontWeight: '450', color: colors.textSecondary }}>Position</th>
                    <th style={{ padding: '14px 16px', textAlign: 'left', fontSize: '14px', fontWeight: '450', color: colors.textSecondary }}>Variable</th>
                    <th style={{ padding: '14px 16px', textAlign: 'center', fontSize: '14px', fontWeight: '450', color: colors.textSecondary }}>Responses</th>
                    <th style={{ padding: '14px 16px', textAlign: 'center', fontSize: '14px', fontWeight: '450', color: colors.textSecondary }}>Retention</th>
                    <th style={{ padding: '14px 16px', textAlign: 'center', fontSize: '14px', fontWeight: '450', color: colors.textSecondary }}>Drop-off</th>
                    <th style={{ padding: '14px 16px', textAlign: 'center', fontSize: '14px', fontWeight: '450', color: colors.textSecondary }}>Severity</th>
                  </tr>
                </thead>
                <tbody>
                  {byVariable.map((item, idx) => {
                    const severityColor = getSeverityColor(item.severity);
                    return (
                      <tr key={item.variable} style={{ borderBottom: `1px solid ${colors.borderLight}` }}>
                        <td style={{ padding: '14px 16px', fontSize: '15px', color: colors.textMuted }}>
                          {item.position + 1}
                        </td>
                        <td style={{ padding: '14px 16px', fontSize: '15px', fontWeight: '450', color: colors.text }}>
                          {item.variable}
                        </td>
                        <td style={{ padding: '14px 16px', textAlign: 'center', fontSize: '15px', color: colors.text }}>
                          {item.responses.toLocaleString()}
                        </td>
                        <td style={{ padding: '14px 16px', textAlign: 'center', fontSize: '15px', fontWeight: '500', color: colors.primary }}>
                          {item.retention_rate.toFixed(1)}%
                        </td>
                        <td style={{ padding: '14px 16px', textAlign: 'center', fontSize: '15px', fontWeight: '500', color: severityColor }}>
                          {item.dropoff_rate > 0 ? `-${item.dropoff_rate.toFixed(1)}%` : '—'}
                        </td>
                        <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                          <span style={{
                            padding: '4px 12px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: '600',
                            backgroundColor: `${severityColor}15`,
                            color: severityColor,
                            textTransform: 'capitalize'
                          }}>
                            {item.severity}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
 
        {/* Early Termination Analysis */}
        {patterns.early_termination && patterns.early_termination.total_early_terminations > 0 && (
          <AnimatedCard delay={80} translateY={12}>
          <div className="nsx-metric-card" style={{
            padding: '18px',
            backgroundColor: colors.cardBg,
            borderRadius: '12px',
            border: `1px solid ${colors.border}`
          }}>
            <h3 style={{ fontSize: '13px', fontWeight: '500', color: colors.text, marginBottom: '12px' }}>
              Early Termination Points
            </h3>
            <p style={{ fontSize: '15px', color: colors.textSecondary, marginBottom: '12px' }}>
              {patterns.early_termination.total_early_terminations} respondents ({patterns.early_termination.early_termination_rate.toFixed(1)}%) stopped before completing the survey
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
              {patterns.early_termination.by_variable
                .filter(v => v.count > 0)
                .sort((a, b) => b.count - a.count)
                .slice(0, 5)
                .map(v => (
                  <div className="nsx-metric-card" key={v.variable} style={{
                    padding: '12px 16px',
                    backgroundColor: colors.cardBgAlt,
                    borderRadius: '8px',
                    border: `1px solid ${colors.border}`
                  }}>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: colors.text }}>
                      {v.variable}
                    </div>
                    <div style={{ fontSize: '12px', color: colors.textMuted }}>
                      {v.count} terminations ({v.percentage.toFixed(1)}%)
                    </div>
                  </div>
                ))}
            </div>
          </div>
          </AnimatedCard>
        )}
      </div>
    );
  };
 
  // ============== COLLECTOR ANALYSIS TAB ==============
  const renderCollectorTab = () => {
    if (!hasCollectorData) return null;
    
    const byCollector = overall.by_collector || {};
    const comparison = collectorComparison || {};
 
    // Export to CSV
    const exportToCSV = () => {
      let csv = 'Collector,Total Observations,Complete Responses,Complete Rate %,Max Drop-off Variable,Max Drop-off %\n';
      sortedCollectors.forEach(c => {
        csv += `${c.id},${c.total_observations},${c.complete_responses},${c.complete_response_rate},${c.max_dropoff?.variable || 'N/A'},${c.max_dropoff?.dropoff_rate || 0}\n`;
      });
      
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'survey_flow_collector_analysis.csv';
      a.click();
      window.URL.revokeObjectURL(url);
    };
    
    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: '16px', marginTop: '80px', overflow: 'hidden', width: '100%' }}>
        {/* Comparison Stats */}
        <AnimatedCard delay={0} translateY={12}>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'minmax(0, 1fr)' : 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))', gap: '16px', minWidth: 0 }}>
          <MetricCard
            colors={colors}
            isDarkMode={isDarkMode}
            isMobile={isMobile}
            animDelay={0}
            icon={Users}
            title="Total Collectors"
            value={Object.keys(byCollector).length}
            subtitle="With survey flow data"
            color={colors.neutral}
          />
          <MetricCard
            colors={colors}
            isDarkMode={isDarkMode}
            isMobile={isMobile}
            animDelay={80}
            icon={Target}
            title="Average Completion"
            value={`${(comparison.average_score || 0).toFixed(1)}%`}
            subtitle="Across all collectors"
            color={getCompletionColor(comparison.average_score || 0)}
            warning={(comparison.average_score || 0) > 100 ? 
              "Average exceeds 100% because some collectors have more respondents answering the last variable than the first. This may indicate skip logic or data issues." : null}
          />
          <MetricCard
            colors={colors}
            isDarkMode={isDarkMode}
            isMobile={isMobile}
            animDelay={160}
            icon={CheckCircle}
            title="Top Performer"
            value={comparison.best_collector || 'N/A'}
            subtitle="Highest completion rate"
            color={colors.success}
          />
          <MetricCard
            colors={colors}
            isDarkMode={isDarkMode}
            isMobile={isMobile}
            animDelay={240}
            icon={AlertTriangle}
            title="Needs Attention"
            value={comparison.needs_attention || 'N/A'}
            subtitle="Lowest completion rate"
            color={comparison.needs_attention ? colors.error : colors.success}
          />
        </div>
        </AnimatedCard>
 
        {/* Collector Table */}
        {collectorView === 'table' && (
        <AnimatedCard delay={160} translateY={12}>
        <div className="nsx-metric-card" style={{
          backgroundColor: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.6)',
          borderRadius: '8px',
          border: `0.5px solid ${isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          overflow: 'hidden'
        }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: colors.cardBgAlt }}>
                  <SortableHeader field="id" label="Collector" />
                  <SortableHeader field="total_observations" label="Observations" align="center" />
                  <SortableHeader field="complete_responses" label="Complete" align="center" />
                  <SortableHeader field="complete_response_rate" label="Complete %" align="center" />
                  <th style={{ padding: '14px 16px', textAlign: 'center', fontSize: '14px', fontWeight: '450', color: colors.textSecondary, borderBottom: `2px solid ${colors.border}` }}>
                    Max Drop-off
                  </th>
                  <th style={{ padding: '14px 16px', textAlign: 'center', fontSize: '14px', fontWeight: '450', color: colors.textSecondary, borderBottom: `2px solid ${colors.border}` }}>
                    Status
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedCollectors.map(collector => {
                  const avgScore = comparison.average_score || 100;
                  const rate = collector.complete_response_rate || 0;
                  const tolerance = 0.1;
                  
                  // Determine status based on comparison to average
                  let statusLabel, statusBadgeColor;
                  if (rate > avgScore + tolerance) {
                    statusLabel = 'Above Avg';
                    statusBadgeColor = colors.success;
                  } else if (rate >= avgScore - tolerance) {
                    statusLabel = 'At Avg';
                    statusBadgeColor = colors.neutral;
                  } else {
                    statusLabel = 'Below Avg';
                    statusBadgeColor = colors.error;
                  }
                  
                  return (
                    <tr key={collector.id} style={{ borderBottom: `1px solid ${colors.borderLight}` }}>
                      <td style={{ padding: '14px 16px', fontSize: '15px', fontWeight: '450', color: colors.text }}>
                        {collector.id}
                      </td>
                      <td style={{ padding: '14px 16px', textAlign: 'center', fontSize: '15px', color: colors.text }}>
                        {collector.total_observations}
                      </td>
                      <td style={{ padding: '14px 16px', textAlign: 'center', fontSize: '15px', color: colors.text }}>
                        {collector.complete_responses}
                      </td>
                      <td style={{ padding: '14px 16px', textAlign: 'center', fontSize: '15px', fontWeight: '500', color: getCompletionColor(rate) }}>
                        {rate.toFixed(1)}%
                      </td>
                      <td style={{ padding: '14px 16px', textAlign: 'center', fontSize: '13px' }}>
                        {collector.max_dropoff ? (
                          <span style={{ color: collector.max_dropoff.dropoff_rate > 15 ? colors.error : colors.textSecondary }}>
                            {collector.max_dropoff.variable} ({collector.max_dropoff.dropoff_rate.toFixed(1)}%)
                          </span>
                        ) : '—'}
                      </td>
                      <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                        <span style={{
                          padding: '4px 12px',
                          borderRadius: '12px',
                          fontSize: '13px',
                          fontWeight: '400',
                          backgroundColor: `${statusBadgeColor}15`,
                          color: statusBadgeColor
                        }}>
                          {statusLabel}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        </AnimatedCard>
        )}
      </div>
    );
  };
 
  // ============== SKIP LOGIC TAB ==============
  const renderSkipLogicTab = () => {
    if (!skipLogicAnalysis) return null;
    
    const stats = skipLogicAnalysis.statistics || {};
    const ruleSummaries = skipLogicAnalysis.rule_summaries || [];
    const violations = skipLogicAnalysis.violations || [];
    const recommendations = skipLogicAnalysis.recommendations || [];
    
    // Sort rule summaries
    const sortedRules = [...ruleSummaries].sort((a, b) => {
      const aVal = a[skipLogicSortField] ?? 0;
      const bVal = b[skipLogicSortField] ?? 0;
      return skipLogicSortDirection === 'desc' ? bVal - aVal : aVal - bVal;
    });
    
    const getSeverityColor = (severity) => {
      const severityColors = {
        'critical': '#EF4444',
        'high': '#F97316',
        'medium': '#F59E0B',
        'low': '#34D399',
        'none': '#10B981'
      };
      return severityColors[severity] || colors.textMuted;
    };
    
    const getSeverityBg = (severity) => {
      const severityBgs = {
        'critical': 'rgba(239, 68, 68, 0.15)',
        'high': 'rgba(249, 115, 22, 0.15)',
        'medium': 'rgba(245, 158, 11, 0.15)',
        'low': 'rgba(52, 211, 153, 0.15)',
        'none': 'rgba(16, 185, 129, 0.15)'
      };
      return severityBgs[severity] || 'transparent';
    };
    
    const toggleRuleExpansion = (ruleIndex) => {
      setExpandedRules(prev => ({
        ...prev,
        [ruleIndex]: !prev[ruleIndex]
      }));
    };
    
    // Get violations for a specific rule
    const getViolationsForRule = (targetQuestion) => {
      return violations.filter(v => v.target_question === targetQuestion).slice(0, 10);
    };
    
    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: '16px', marginTop: '80px', overflow: 'hidden', width: '100%' }}>
        {/* Summary Cards */}
        <AnimatedCard delay={0} translateY={12}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)',
          gap: '16px'
        }}>
          <MetricCard
            colors={colors}
            isDarkMode={isDarkMode}
            isMobile={isMobile}
            animDelay={0}
            icon={AlertTriangle}
            iconColor={stats.total_violations > 0 ? colors.error : colors.success}
            title="Total Violations"
            value={stats.total_violations?.toLocaleString() || 0}
            subtitle={`${stats.overall_violation_rate?.toFixed(1) || 0}% violation rate`}
          />
          <MetricCard
            colors={colors}
            isDarkMode={isDarkMode}
            isMobile={isMobile}
            animDelay={80}
            icon={FileWarning}
            iconColor={colors.warning}
            title="Rules with Violations"
            value={stats.rules_with_violations || 0}
            subtitle={`of ${stats.rules_evaluated || 0} rules evaluated`}
          />
          <MetricCard
            colors={colors}
            isDarkMode={isDarkMode}
            isMobile={isMobile}
            animDelay={160}
            icon={Users}
            iconColor={colors.purple}
            title="Affected Rows"
            value={stats.rows_with_violations?.toLocaleString() || 0}
            subtitle={`${((stats.rows_with_violations / (data?.overall?.total_responses || 1)) * 100).toFixed(1)}% of responses`}
          />
          <MetricCard
            colors={colors}
            isDarkMode={isDarkMode}
            isMobile={isMobile}
            animDelay={240}
            icon={stats.total_violations === 0 ? CheckCircle : Shield}
            iconColor={stats.total_violations === 0 ? colors.success : colors.primary}
            title="Skip Logic Status"
            value={stats.total_violations === 0 ? 'Clean' : 'Issues Found'}
            subtitle={stats.total_violations === 0 ? 'All rules passed' : 'Review violations below'}
          />
        </div>
        </AnimatedCard>
        
        {/* Recommendations */}
        {recommendations.length > 0 && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(350px, 1fr))',
            gap: '16px'
          }}>
            {recommendations.slice(0, 3).map((rec, idx) => (
              <InsightCard
                key={idx}
                type={rec.type === 'error' ? 'warning' : rec.type === 'success' ? 'success' : 'info'}
                title={rec.message}
                description={rec.detail}
              />
            ))}
          </div>
        )}
        
        {/* Rules Table */}
        <AnimatedCard delay={80} translateY={12}>
        <div className="nsx-metric-card" style={{
          backgroundColor: colors.cardBg,
          borderRadius: '16px',
          border: `1px solid ${colors.border}`,
          overflow: 'hidden'
        }}>
          <div style={{
            padding: '20px 24px',
            borderBottom: `1px solid ${colors.border}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div>
              <h3 style={{ fontSize: '14px', fontWeight: '600', color: colors.text, margin: 0 }}>
                Skip Logic Rules Analysis
              </h3>
              <p style={{ fontSize: '13px', color: colors.textMuted, margin: '4px 0 0 0' }}>
                Evaluation of {stats.rules_evaluated || 0} skip logic rules from XLSForm
              </p>
            </div>
          </div>
          
          {sortedRules.length === 0 ? (
            <div style={{ padding: '32px', textAlign: 'center' }}>
              <CheckCircle size={48} style={{ color: colors.success, marginBottom: '12px' }} />
              <p style={{ fontSize: '14px', color: colors.text, margin: 0 }}>
                No skip logic rules found in the XLSForm
              </p>
              <p style={{ fontSize: '13px', color: colors.textMuted, marginTop: '8px' }}>
                Upload an XLSForm with skip logic rules to see analysis here
              </p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: isDarkMode ? '#1F1F23' : '#F9FAFB' }}>
                    <th style={{ 
                      padding: '14px 16px', 
                      textAlign: 'left', 
                      fontSize: '13px', 
                      fontWeight: '600',
                      color: colors.textSecondary,
                      borderBottom: `1px solid ${colors.border}`,
                      width: '40px'
                    }}>
                    </th>
                    <th style={{ 
                      padding: '14px 16px', 
                      textAlign: 'left', 
                      fontSize: '13px', 
                      fontWeight: '600',
                      color: colors.textSecondary,
                      borderBottom: `1px solid ${colors.border}`
                    }}>
                      Target Question
                    </th>
                    <th style={{ 
                      padding: '14px 16px', 
                      textAlign: 'left', 
                      fontSize: '13px', 
                      fontWeight: '600',
                      color: colors.textSecondary,
                      borderBottom: `1px solid ${colors.border}`,
                      maxWidth: '300px'
                    }}>
                      Skip Condition
                    </th>
                    <th 
                      style={{ 
                        padding: '14px 16px', 
                        textAlign: 'center', 
                        fontSize: '13px', 
                        fontWeight: '600',
                        color: skipLogicSortField === 'total_violations' ? colors.primary : colors.textSecondary,
                        borderBottom: `1px solid ${colors.border}`,
                        cursor: 'pointer'
                      }}
                      onClick={() => {
                        if (skipLogicSortField === 'total_violations') {
                          setSkipLogicSortDirection(d => d === 'desc' ? 'asc' : 'desc');
                        } else {
                          setSkipLogicSortField('total_violations');
                          setSkipLogicSortDirection('desc');
                        }
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                        Violations
                        <ArrowUpDown size={14} />
                      </div>
                    </th>
                    <th style={{ 
                      padding: '14px 16px', 
                      textAlign: 'center', 
                      fontSize: '13px', 
                      fontWeight: '600',
                      color: colors.textSecondary,
                      borderBottom: `1px solid ${colors.border}`
                    }}>
                      Severity
                    </th>
                    <th style={{ 
                      padding: '14px 16px', 
                      textAlign: 'center', 
                      fontSize: '13px', 
                      fontWeight: '600',
                      color: colors.textSecondary,
                      borderBottom: `1px solid ${colors.border}`
                    }}>
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedRules.map((rule, idx) => {
                    const isExpanded = expandedRules[idx];
                    const ruleViolations = getViolationsForRule(rule.target_question);
                    const hasViolations = rule.total_violations > 0;
                    
                    return (
                      <React.Fragment key={idx}>
                        <tr 
                          style={{ 
                            backgroundColor: isExpanded ? (isDarkMode ? '#1F1F23' : '#F9FAFB') : 'transparent',
                            cursor: hasViolations ? 'pointer' : 'default',
                            transition: 'background-color 0.15s'
                          }}
                          onClick={() => hasViolations && toggleRuleExpansion(idx)}
                        >
                          <td style={{ 
                            padding: '14px 16px', 
                            borderBottom: isExpanded ? 'none' : `1px solid ${colors.border}`,
                            color: colors.textMuted
                          }}>
                            {hasViolations && (
                              isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />
                            )}
                          </td>
                          <td style={{ 
                            padding: '14px 16px', 
                            borderBottom: isExpanded ? 'none' : `1px solid ${colors.border}`,
                            fontSize: '14px',
                            color: colors.text,
                            fontWeight: '500'
                          }}>
                            {rule.target_question}
                          </td>
                          <td style={{ 
                            padding: '14px 16px', 
                            borderBottom: isExpanded ? 'none' : `1px solid ${colors.border}`,
                            fontSize: '13px',
                            color: colors.textSecondary,
                            maxWidth: '300px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                          title={rule.expression}
                          >
                            <code style={{ 
                              backgroundColor: isDarkMode ? '#2A2A2E' : '#F3F4F6',
                              padding: '2px 6px',
                              borderRadius: '4px',
                              fontSize: '12px'
                            }}>
                              {rule.expression?.length > 50 ? rule.expression.substring(0, 47) + '...' : rule.expression}
                            </code>
                          </td>
                          <td style={{ 
                            padding: '14px 16px', 
                            borderBottom: isExpanded ? 'none' : `1px solid ${colors.border}`,
                            textAlign: 'center',
                            fontSize: '14px',
                            fontWeight: '600',
                            color: hasViolations ? colors.error : colors.success
                          }}>
                            {rule.total_violations || 0}
                          </td>
                          <td style={{ 
                            padding: '14px 16px', 
                            borderBottom: isExpanded ? 'none' : `1px solid ${colors.border}`,
                            textAlign: 'center'
                          }}>
                            <span style={{
                              padding: '4px 10px',
                              borderRadius: '12px',
                              fontSize: '12px',
                              fontWeight: '500',
                              backgroundColor: getSeverityBg(rule.severity),
                              color: getSeverityColor(rule.severity),
                              textTransform: 'capitalize'
                            }}>
                              {rule.severity || 'none'}
                            </span>
                          </td>
                          <td style={{ 
                            padding: '14px 16px', 
                            borderBottom: isExpanded ? 'none' : `1px solid ${colors.border}`,
                            textAlign: 'center'
                          }}>
                            {rule.status === 'not_evaluated' ? (
                              <span style={{ color: colors.textMuted, fontSize: '13px' }}>
                                Not Evaluated
                              </span>
                            ) : hasViolations ? (
                              <XCircle size={18} style={{ color: colors.error }} />
                            ) : (
                              <CheckCircle size={18} style={{ color: colors.success }} />
                            )}
                          </td>
                        </tr>
                        
                        {/* Expanded violation details */}
                        {isExpanded && hasViolations && (
                          <tr>
                            <td colSpan={6} style={{ 
                              padding: '0 16px 16px 16px',
                              backgroundColor: isDarkMode ? '#1F1F23' : '#F9FAFB',
                              borderBottom: `1px solid ${colors.border}`
                            }}>
                              <div className="nsx-metric-card" style={{
                                backgroundColor: colors.cardBg,
                                borderRadius: '8px',
                                border: `1px solid ${colors.border}`,
                                padding: '16px',
                                marginLeft: '24px'
                              }}>
                                <div style={{ 
                                  fontSize: '13px', 
                                  fontWeight: '600', 
                                  color: colors.text, 
                                  marginBottom: '12px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px'
                                }}>
                                  <AlertCircle size={14} style={{ color: colors.warning }} />
                                  Sample Violations (showing up to 10)
                                </div>
                                <div style={{ 
                                  display: 'grid', 
                                  gap: '8px',
                                  maxHeight: '200px',
                                  overflowY: 'auto'
                                }}>
                                  {ruleViolations.map((violation, vIdx) => (
                                    <div 
                                      key={vIdx}
                                      style={{
                                        padding: '10px 12px',
                                        backgroundColor: isDarkMode ? '#2A2A2E' : '#F9FAFB',
                                        borderRadius: '6px',
                                        fontSize: '13px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '16px'
                                      }}
                                    >
                                      <span style={{ 
                                        color: colors.textMuted,
                                        minWidth: '70px'
                                      }}>
                                        Row {violation.row_index}
                                      </span>
                                      <span style={{ color: colors.text }}>
                                        Answered: <strong>{violation.target_value}</strong>
                                      </span>
                                      {violation.source_values && Object.keys(violation.source_values).length > 0 && (
                                        <span style={{ color: colors.textSecondary }}>
                                          | Condition values: {Object.entries(violation.source_values).map(([k, v]) => `${k}=${v}`).join(', ')}
                                        </span>
                                      )}
                                    </div>
                                  ))}
                                </div>
                                {rule.total_violations > 10 && (
                                  <p style={{ 
                                    fontSize: '12px', 
                                    color: colors.textMuted, 
                                    marginTop: '8px',
                                    marginBottom: 0
                                  }}>
                                    + {rule.total_violations - 10} more violations not shown
                                  </p>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
        </AnimatedCard>
        
        {/* Quality Score Impact */}
        {skipLogicAnalysis.statistics?.overall_violation_rate > 0 && (
          <div className="nsx-metric-card" style={{
            backgroundColor: colors.cardBg,
            borderRadius: '16px',
            border: `1px solid ${colors.border}`,
            padding: '20px 24px'
          }}>
            <h3 style={{ fontSize: '14px', fontWeight: '600', color: colors.text, margin: '0 0 12px 0' }}>
              Impact on Flow Quality Score
            </h3>
            <p style={{ fontSize: '13px', color: colors.textSecondary, margin: 0 }}>
              Skip logic violations have reduced the Flow Quality Score by{' '}
              <span style={{ color: colors.error, fontWeight: '600' }}>
                {Math.min(20, (skipLogicAnalysis.statistics.overall_violation_rate * 2)).toFixed(1)} points
              </span>
              {' '}(max penalty: 20 points). Fixing these violations will improve your overall data quality assessment.
            </p>
          </div>
        )}
      </div>
    );
  };
 
  // ============== MAIN RENDER ==============
  return (
    <div 
      className="survey-flow-section activeQualityDetector"
      style={{
        width: '100%',
        maxWidth: '100%',
        padding: isMobile ? '0 8px 24px 8px' : '0 45px 24px 45px',
        boxSizing: 'border-box',
        overflow: 'hidden',
        overflowX: 'clip',  // stronger than hidden; prevents any descendant from widening the page
      }}
    >
      <DimensionHeader
        slug="survey flow"
        title="Survey Flow"
        description="Traces how respondents moved through your form. Exposes drop-offs, skip-logic violations, and unusual navigation patterns that can compromise data integrity."
        rememberCollapseKey="survey_flow"
        stat={{
          score: qualityScore.overall_score,
          scoreLabel: 'flow score',
          unit: '%',
        }}
        tabs={tabs.map(t => {
          if (t.id === 'paths')      return { ...t, count: pathAnalysis.unique_paths || 0, countTone: 'neutral' };
          if (t.id === 'collectors') return { ...t, count: collectors.length, countTone: 'neutral' };
          return t;
        })}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        isMobile={isMobile}
        isSidePanelExpanded={isSidePanelExpanded}
      />
      <div style={{ 
          minHeight: '600px',
          marginTop: '16px'
        }}>
          {activeTab === 'overview' && renderOverviewTab()}
          {activeTab === 'paths' && renderPathExplorerTab()}
          {activeTab === 'dropoff' && renderDropoffTab()}
          {activeTab === 'collectors' && hasCollectorData && renderCollectorTab()}
          {activeTab === 'skiplogic' && hasSkipLogicData && renderSkipLogicTab()}
      </div>
    </div>
  );
};
 
export default SurveyFlowDashboard;

{/*
KII:
- check who to insterview at the provincial level related to immunization (Provincial Health Officers, Immunization Program Managers, and Public Health Nurses)
  Mwiinga
- identify a community leader: religious leader, village headman, or other influential community member. community health Worker.
- identify a caregiver
surveyin protocol 


EPI: Organize call with EPI to discuss survey participants


CIDRZ
Cheryl availability for a call
No Monday:
Tuesday 1 - 3 PM
Wednesday anytime   | except 2 PM for Anastasia
Thursday before 3 pm
Friday anytme


MoU: IPA and EPI program
Draft the MoU IPA legal takes time.
But we need to follow up.
*/}